"use strict";
/**
 * In KILT, an [[AttestedClaim]] is a **credential**, which a Claimer can store locally and share with Verifiers as they wish.
 *
 * Once a [[RequestForAttestation]] has been made, the [[Attestation]] can be built and the Attester submits it wrapped in an [[AttestedClaim]] object.
 * This [[AttestedClaim]] also contains the original request for attestation.
 * RequestForAttestation also exposes a [[createPresentation]] method, that can be used by the claimer to hide some specific information from the verifier for more privacy.
 *
 * @packageDocumentation
 * @module AttestedClaim
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const utils_1 = require("@kiltprotocol/utils");
const Attestation_1 = tslib_1.__importDefault(require("../attestation/Attestation"));
const RequestForAttestation_1 = tslib_1.__importDefault(require("../requestforattestation/RequestForAttestation"));
const AttestedClaim_utils_1 = tslib_1.__importDefault(require("./AttestedClaim.utils"));
class AttestedClaim {
    /**
     * Builds a new [[AttestedClaim]] instance.
     *
     * @param attestedClaimInput - The base object with all required input, from which to create the attested claim.
     * @example ```javascript
     * // Create an `AttestedClaim` upon successful `Attestation` creation:
     * const credential = new AttestedClaim(attestedClaimInput);
     * ```
     */
    constructor(attestedClaimInput) {
        AttestedClaim_utils_1.default.errorCheck(attestedClaimInput);
        this.request = RequestForAttestation_1.default.fromRequest(attestedClaimInput.request);
        this.attestation = Attestation_1.default.fromAttestation(attestedClaimInput.attestation);
    }
    /**
     * [STATIC] Builds an instance of [[AttestedClaim]], from a simple object with the same properties.
     * Used for deserialization.
     *
     * @param attestedClaimInput - The base object from which to create the attested claim.
     * @returns A new instantiated [[AttestedClaim]] object.
     * @example ```javascript
     * // create an AttestedClaim object, so we can call methods on it (`serialized` is a serialized AttestedClaim object)
     * AttestedClaim.fromAttestedClaim(JSON.parse(serialized));
     * ```
     */
    static fromAttestedClaim(attestedClaimInput) {
        return new AttestedClaim(attestedClaimInput);
    }
    /**
     * [STATIC] Builds a new instance of [[AttestedClaim]], from all required properties.
     *
     * @param request - The request for attestation for the claim that was attested.
     * @param attestation - The attestation for the claim by the attester.
     * @returns A new [[AttestedClaim]] object.
     * @example ```javascript
     * // create an AttestedClaim object after receiving the attestation from the attester
     * AttestedClaim.fromRequestAndAttestation(request, attestation);
     * ```
     */
    static fromRequestAndAttestation(request, attestation) {
        return new AttestedClaim({
            request,
            attestation,
        });
    }
    /**
     *  [STATIC] Custom Type Guard to determine input being of type IAttestedClaim using the AttestedClaimUtils errorCheck.
     *
     * @param input The potentially only partial IAttestedClaim.
     *
     * @returns Boolean whether input is of type IAttestedClaim.
     */
    static isIAttestedClaim(input) {
        try {
            AttestedClaim_utils_1.default.errorCheck(input);
        }
        catch (error) {
            return false;
        }
        return true;
    }
    /**
     * (ASYNC) Verifies whether the attested claim is valid. It is valid if:
     * * the data is valid (see [[verifyData]]);
     * and
     * * the [[Attestation]] object for this attested claim is valid (see [[Attestation.checkValidity]], where the **chain** is queried).
     *
     * Upon presentation of an attested claim, a verifier would call this [[verify]] function.
     *
     * @param attestedClaim - The attested claim to check for validity.
     * @returns A promise containing whether this attested claim is valid.
     * @example ```javascript
     * attestedClaim.verify().then((isVerified) => {
     *   // `isVerified` is true if the attestation is verified, false otherwise
     * });
     * ```
     */
    static async verify(attestedClaim) {
        return (AttestedClaim.verifyData(attestedClaim) &&
            Attestation_1.default.checkValidity(attestedClaim.attestation));
    }
    async verify() {
        return AttestedClaim.verify(this);
    }
    /**
     * Verifies whether the data of the given attested claim is valid. It is valid if:
     * * the [[RequestForAttestation]] object associated with this attested claim has valid data (see [[RequestForAttestation.verifyData]]);
     * and
     * * the hash of the [[RequestForAttestation]] object for the attested claim, and the hash of the [[Claim]] for the attested claim are the same.
     *
     * @param attestedClaim - The attested claim to verify.
     * @returns Whether the attested claim's data is valid.
     * @example ```javascript
     * const verificationResult = attestedClaim.verifyData();
     * ```
     */
    static verifyData(attestedClaim) {
        if (attestedClaim.request.claim.cTypeHash !==
            attestedClaim.attestation.cTypeHash)
            return false;
        return (attestedClaim.request.rootHash === attestedClaim.attestation.claimHash &&
            RequestForAttestation_1.default.verifyData(attestedClaim.request));
    }
    verifyData() {
        return AttestedClaim.verifyData(this);
    }
    /**
     *  [STATIC] Verifies the data of each element of the given Array of IAttestedClaims.
     *
     * @param legitimations Array of IAttestedClaims to validate.
     * @throws [[ERROR_LEGITIMATIONS_UNVERIFIABLE]] when one of the IAttestedClaims data is unable to be verified.
     *
     * @returns Boolean whether each element of the given Array of IAttestedClaims is verifiable.
     */
    static validateLegitimations(legitimations) {
        legitimations.forEach((legitimation) => {
            if (!AttestedClaim.verifyData(legitimation)) {
                throw utils_1.SDKErrors.ERROR_LEGITIMATIONS_UNVERIFIABLE();
            }
        });
        return true;
    }
    /**
     * Gets the hash of the claim that corresponds to this attestation.
     *
     * @returns The hash of the claim for this attestation (claimHash).
     * @example ```javascript
     * attestation.getHash();
     * ```
     */
    getHash() {
        return this.attestation.claimHash;
    }
    getAttributes() {
        // TODO: move this to claim or contents
        return new Set(Object.keys(this.request.claim.contents));
    }
    /**
     * Creates a public presentation which can be sent to a verifier.
     *
     * @param publicAttributes All properties of the claim which have been requested by the verifier and therefore must be publicly presented.
     * If kept empty, we hide all attributes inside the claim for the presentation.
     *
     * @returns A deep copy of the AttestedClaim with all but `publicAttributes` removed.
     */
    createPresentation(publicAttributes) {
        const attClaim = new AttestedClaim(
        // clone the attestation and request for attestation because properties will be deleted later.
        // TODO: find a nice way to clone stuff
        JSON.parse(JSON.stringify(this)));
        // filter attributes that are not in public attributes
        const excludedClaimProperties = Array.from(this.getAttributes()).filter((property) => !publicAttributes.includes(property));
        // remove these attributes
        attClaim.request.removeClaimProperties(excludedClaimProperties);
        return attClaim;
    }
    /**
     * Compresses an [[AttestedClaim]] object.
     *
     * @returns An array that contains the same properties of an [[AttestedClaim]].
     */
    compress() {
        return AttestedClaim_utils_1.default.compress(this);
    }
    /**
     * [STATIC] Builds an [[AttestedClaim]] from the decompressed array.
     *
     * @param attestedClaim The [[CompressedAttestedClaim]] that should get decompressed.
     * @returns A new [[AttestedClaim]] object.
     */
    static decompress(attestedClaim) {
        const decompressedAttestedClaim = AttestedClaim_utils_1.default.decompress(attestedClaim);
        return AttestedClaim.fromAttestedClaim(decompressedAttestedClaim);
    }
}
exports.default = AttestedClaim;
